<div class="flex h-dvh flex-col overflow-clip bg-background text-foreground">
    <a
        class="sr-only fixed top-3 left-3 z-50 rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground focus:not-sr-only focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
        href="#main-content">
        Skip to main content
    </a>
    <header class="sticky top-0 z-10 shrink-0 border-b border-border bg-background/95 backdrop-blur">
        <nav class="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-3 py-3 sm:px-4">
            <a
                class="flex min-h-11 shrink-0 items-center gap-2 font-display text-xs font-bold tracking-[0.1em] text-foreground uppercase sm:min-h-8 sm:text-sm sm:tracking-[0.2em]"
                routerLink="/">
                <img class="size-8 rounded-sm" src="/brand/animus-logo.svg" alt="" aria-hidden="true" />
                <span>The <span class="text-destructive">Animus</span></span>
            </a>

            <!-- Inline nav (md+): icon-only tabs at md, full labels at lg, theme toggle at the end -->
            <div
                class="hidden items-center gap-1 text-xs font-medium text-muted-foreground md:flex md:gap-1.5 lg:gap-2 lg:text-sm">
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    [routerLinkActiveOptions]="{ exact: true }"
                    routerLink="/"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideHome" size="14px" />
                    <span class="hidden lg:inline">Home</span>
                    <span class="sr-only md:hidden lg:hidden">Home</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/library/rules"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideScrollText" size="14px" />
                    <span class="hidden lg:inline">Rules</span>
                    <span class="sr-only md:hidden lg:hidden">Rules</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/library/factions"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideFlag" size="14px" />
                    <span class="hidden lg:inline">Factions</span>
                    <span class="sr-only md:hidden lg:hidden">Factions</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/library/custom-spawn"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideDices" size="14px" />
                    <span class="hidden lg:inline">Custom Spawn</span>
                    <span class="sr-only md:hidden lg:hidden">Custom Spawn</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/library/resupply-table"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucidePackage" size="14px" />
                    <span class="hidden lg:inline">Resupply Table</span>
                    <span class="sr-only md:hidden lg:hidden">Resupply Table</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/about"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideInfo" size="14px" />
                    <span class="hidden lg:inline">About</span>
                    <span class="sr-only md:hidden lg:hidden">About</span>
                </a>
                <a
                    class="inline-flex min-h-11 items-center gap-1.5 px-2 transition-colors hover:text-foreground lg:min-h-8"
                    routerLink="/settings"
                    routerLinkActive="text-foreground">
                    <ng-icon class="hidden md:inline-block" name="lucideSettings" size="14px" />
                    <span class="hidden lg:inline">Settings</span>
                    <span class="sr-only md:hidden lg:hidden">Settings</span>
                </a>
                <button
                    class="flex size-11 items-center justify-center rounded-md border border-border transition-colors hover:bg-accent hover:text-foreground lg:size-9"
                    [attr.aria-label]="theme.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
                    [attr.aria-pressed]="theme.isDark()"
                    (click)="theme.toggle()"
                    type="button"
                    title="Toggle light / dark mode">
                    @if (theme.isDark()) {
                        <ng-icon name="lucideSun" size="16px" />
                    } @else {
                        <ng-icon name="lucideMoon" size="16px" />
                    }
                </button>
            </div>

            <!-- Mobile / tablet portrait: accessible navigation sheet + theme toggle -->
            <div class="flex flex-1 items-center justify-end gap-1 md:hidden">
                <hlm-sheet>
                    <button
                        class="flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-foreground"
                        aria-label="Menu - Open navigation"
                        hlmSheetTrigger
                        side="right"
                        type="button">
                        <ng-icon name="lucideMenu" size="18px" />
                        <span>Menu</span>
                    </button>
                    <hlm-sheet-content *hlmSheetPortal side="right">
                        <div class="px-6 pt-6">
                            <h2 class="text-lg font-semibold" hlmSheetTitle>Navigation</h2>
                            <p class="mt-1 text-sm text-muted-foreground" hlmSheetDescription>
                                Browse The Animus.
                            </p>
                        </div>
                        <nav class="flex flex-col gap-1 px-4 pb-6" aria-label="Mobile navigation">
                            @for (item of navItems; track item.path) {
                                <button
                                    class="min-h-11 rounded-md px-3 text-left text-sm font-medium transition-colors hover:bg-accent hover:text-foreground"
                                    [class.bg-accent]="currentNav() === item.path"
                                    (click)="navTo(item.path)"
                                    hlmSheetClose
                                    type="button">
                                    {{ item.label }}
                                </button>
                            }
                        </nav>
                    </hlm-sheet-content>
                </hlm-sheet>
                <button
                    class="flex size-11 items-center justify-center rounded-md border border-border transition-colors hover:bg-accent hover:text-foreground"
                    [attr.aria-label]="theme.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
                    [attr.aria-pressed]="theme.isDark()"
                    (click)="theme.toggle()"
                    type="button"
                    title="Toggle light / dark mode">
                    @if (theme.isDark()) {
                        <ng-icon name="lucideSun" size="16px" />
                    } @else {
                        <ng-icon name="lucideMoon" size="16px" />
                    }
                </button>
            </div>
        </nav>
    </header>
    @if (aboutBannerVisible()) {
        <aside
            class="border-b border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-muted-foreground"
            aria-label="About The Animus">
            <div class="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <p class="text-center">
                    New here? Read about
                    <a
                        class="font-medium text-foreground underline underline-offset-4 hover:text-foreground"
                        routerLink="/about">
                        Horde Mode and the app
                    </a>
                </p>
                <button
                    class="flex size-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-foreground"
                    (click)="dismissAboutBanner()"
                    aria-label="Dismiss about banner"
                    type="button">
                    <ng-icon name="lucideX" size="14px" />
                </button>
            </div>
        </aside>
    }
    @if (storageStatus() === 'unavailable') {
        <aside
            class="border-b border-amber-600/40 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
            aria-live="polite">
            <div class="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-3">
                <span>Saved games are unavailable. Reference content remains available in read-only mode.</span>
                <button class="font-semibold underline underline-offset-4" (click)="retryStorage()" type="button">
                    Retry storage
                </button>
                <button
                    class="font-semibold underline underline-offset-4"
                    (click)="exportStoredData()"
                    type="button">
                    Export stored data
                </button>
            </div>
        </aside>
    }
    <main class="flex min-h-0 flex-1 flex-col overflow-y-auto" id="main-content" tabindex="-1">
        <router-outlet />
    </main>
    <footer
        class="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-border px-4 py-4 text-center text-xs text-muted-foreground"
        [class.justify-between]="offlineReadiness.isUpdateReady()">
        <button
            class="inline-flex items-center gap-1 underline underline-offset-4 hover:text-foreground"
            [attr.aria-label]="'Offline Ready - ' + offlineReadiness.label()"
            (click)="openOfflineStatus()"
            type="button">
            <ng-icon name="lucideCloudOff" size="14px" />
            <span>Offline Ready</span>
            @if (offlineReadiness.isReady()) {
                <ng-icon class="text-emerald-600 dark:text-emerald-400" name="lucideCircleCheck" size="14px" />
            } @else {
                <ng-icon class="text-muted-foreground" name="lucideCircleX" size="14px" />
            }
        </button>
        @if (offlineReadiness.isUpdateReady()) {
            <button
                class="inline-flex items-center gap-1 underline underline-offset-4 hover:text-foreground"
                (click)="openUpdateAvailable()"
                type="button">
                <ng-icon name="lucideRefreshCw" size="14px" />
                <span>Update Available</span>
            </button>
        }
        <button
            class="underline underline-offset-4 hover:text-foreground"
            (click)="openLicencesAndRights()"
            type="button">
            Licences and Rights
        </button>
    </footer>
</div>
